import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { EncryptionUtils } from "./EncryptionUtils.ts";
import Cookies from "js-cookie";

// Flag para controlar logs (facilita desativar em produção)
const DEBUG_MODE = process.env.NODE_ENV === "development";
const AMBIENTE_DEV =
    window.location.hostname === "dayape.dev.apesolutiontech.com";

// Flag para controlar se deve abortar requisições ao desmontar
// (Desativando temporariamente para resolver o problema)
const ABORT_ON_UNMOUNT = false;

// Tempos de cache para diferentes tipos de dados (em milissegundos)
const CACHE_TIMES = {
    APPOINTMENTS: 60000, // 1 minuto
    SERVICES: 300000, // 5 minutos
    USERS: 300000, // 5 minutos
};

let BASE_URL_DEBUG: string | null = "";
if (DEBUG_MODE || AMBIENTE_DEV) {
    console.log(
        "[API] Modo de desenvolvimento ativo: logs detalhados habilitados"
    );
    if (!localStorage.getItem("BASE_URL_DEBUG")) {
        console.warn(
            "[API] BASE_URL_DEBUG não está definido, usando URL padrão de desenvolvimento"
        );
        localStorage.setItem(
            "BASE_URL_DEBUG",
            "https://dayape.api.dev.apesolutiontech.com"
        );
    } else {
        console.log(
            "[API] BASE_URL_DEBUG encontrado:",
            localStorage.getItem("BASE_URL_DEBUG")
        );
        BASE_URL_DEBUG = localStorage.getItem("BASE_URL_DEBUG");
    }
}

// Configuration constants from environment variables
const API_CONFIG = {
    BASE_URL: BASE_URL_DEBUG || process.env.REACT_APP_API_BASE_URL,
    AUTH_TOKEN_KEY: "tokenizacao",
    SECRET_KEY: process.env.REACT_APP_SECRET_KEY || "",
    USE_SECURE_COOKIES: process.env.REACT_APP_USE_SECURE_COOKIES === "true",
    TOKEN_EXPIRATION_MINUTES: parseInt(
        process.env.REACT_APP_TOKEN_EXPIRATION_MINUTES || "30",
        10
    ),
};

// Validar se as variáveis de ambiente necessárias estão definidas
if (!API_CONFIG.SECRET_KEY) {
    console.error(
        "AVISO DE SEGURANÇA: A variável de ambiente REACT_APP_SECRET_KEY não está definida!"
    );
}

// Estrutura para o cache
interface CacheData {
    data: any;
    timestamp: number;
}

interface CacheStore {
    [key: string]: CacheData;
}

// Gerenciador de cache global
const globalCache: CacheStore = {};

// Importa o debugConsole global (será inicializado quando disponível)
let debugConsoleApi: any = null;

// Tenta carregar o debugConsole de forma lazy
const getDebugConsole = () => {
    if (!debugConsoleApi) {
        try {
            // Import dinâmico para evitar dependência circular
            const module = require('./DebugConsole.js');
            debugConsoleApi = module.debugConsole;
        } catch {
            // Ignora se não conseguir importar
        }
    }
    return debugConsoleApi;
};

// Contador global para ID de requisição
let requestIdCounter = 0;

// Sistema de logs estruturado para debugging
const apiLogger = {
    // Remove dados sensíveis de objetos
    sanitize: (arg: any): any => {
        if (arg && typeof arg === "object") {
            const safeCopy = { ...arg };
            const sensitiveFields = ["password", "token", "authorization", "secret", "key", "senha"];
            Object.keys(safeCopy).forEach((key) => {
                if (sensitiveFields.some((field) => key.toLowerCase().includes(field))) {
                    safeCopy[key] = "***";
                }
            });
            return safeCopy;
        }
        return arg;
    },

    // Log interno que envia para console e DebugConsole
    _log: (emoji: string, category: string, message: string, data?: any, type: 'info' | 'warn' | 'error' = 'info') => {
        if (!(DEBUG_MODE || AMBIENTE_DEV)) return;

        const safeData = data ? apiLogger.sanitize(data) : null;
        const dataStr = safeData ? ` ${JSON.stringify(safeData)}` : '';
        const fullMsg = `${emoji} [${category}] ${message}${dataStr}`;

        // Console normal - inclui objeto para inspeção no DevTools
        if (type === 'error') {
            console.error(fullMsg);
            if (safeData) console.error('📦 Objeto:', safeData);
        } else if (type === 'warn') {
            console.warn(fullMsg);
            if (safeData) console.warn('📦 Objeto:', safeData);
        } else {
            console.log(fullMsg);
            if (safeData) console.log('📦 Objeto:', safeData);
        }

        // DebugConsole visual
        const dc = getDebugConsole();
        if (dc) {
            if (type === 'error') dc.error(fullMsg);
            else if (type === 'warn') dc.warn(fullMsg);
            else dc.log(fullMsg);
        }
    },

    // Gera um novo ID de requisição
    newRequestId: (): number => {
        return ++requestIdCounter;
    },

    // ===== MÉTODOS DE LOG ESPECÍFICOS =====

    // Nova requisição iniciando
    startRequest: (id: number, method: string, route: string, body?: any) => {
        apiLogger._log('', '', `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
        apiLogger._log('🆕', `REQ #${id}`, `${method} /${route}`);
        if (body) {
            const safeBody = apiLogger.sanitize(body);
            apiLogger._log('📋', `REQ #${id}`, `Body: ${JSON.stringify(safeBody)}`, safeBody);
        }
    },

    // URL sendo enviada
    sending: (id: number, url: string) => {
        apiLogger._log('📤', `REQ #${id}`, `URL: ${url}`);
    },

    // Resposta recebida
    response: (id: number, method: string, route: string, status: number, ok: boolean, duration?: number) => {
        const emoji = ok ? '✅' : '❌';
        const durationStr = duration ? ` (${duration}ms)` : '';
        apiLogger._log(emoji, `REQ #${id}`, `${method} /${route} → ${status} ${ok ? 'OK' : 'FAIL'}${durationStr}`);
    },

    // Dados da resposta
    responseData: (id: number, data: any) => {
        const safeData = apiLogger.sanitize(data);
        const dataStr = JSON.stringify(safeData);
        // Truncar se muito grande para a mensagem
        const truncated = dataStr.length > 200 ? dataStr.substring(0, 200) + '...' : dataStr;
        apiLogger._log('📥', `REQ #${id}`, `Data: ${truncated}`, safeData);
    },

    // Fim da requisição
    endRequest: (id: number, success: boolean) => {
        const emoji = success ? '✓' : '✗';
        apiLogger._log('🏁', `REQ #${id}`, `Finalizada ${emoji}`);
    },

    // Cache hit
    cacheHit: (method: string, route: string) => {
        apiLogger._log('💾', 'CACHE', `HIT: ${method} /${route} (usando dados em cache)`);
    },

    // Cache invalidado
    cacheInvalidate: (pattern: string, count: number) => {
        apiLogger._log('🗑️', 'CACHE', `Invalidando ${count} entrada(s): ${pattern}`);
    },

    // Requisição pendente reaproveitada
    pendingReuse: (method: string, route: string) => {
        apiLogger._log('♻️', 'REUSO', `Reaproveitando ${method} /${route} em andamento`);
    },

    // Erro
    error: (id: number, context: string, error: any) => {
        const msg = error instanceof Error ? error.message : String(error);
        apiLogger._log('💥', `REQ #${id}`, `ERRO: ${context} - ${msg}`, null, 'error');
    },

    // Aviso
    warn: (message: string, data?: any) => {
        apiLogger._log('⚠️', 'WARN', message, data, 'warn');
    },

    // Info genérico
    info: (message: string, data?: any) => {
        apiLogger._log('ℹ️', 'INFO', message, data);
    },

    // Auth
    auth: (id: number, message: string) => {
        apiLogger._log('🔐', `REQ #${id}`, `AUTH: ${message}`, null, 'warn');
    },

    // Requisição abortada
    aborted: (id: number, route: string) => {
        apiLogger._log('🛑', `REQ #${id}`, `CANCELADA: /${route}`, null, 'warn');
    },

    // Lifecycle
    lifecycle: (message: string) => {
        apiLogger._log('🔄', 'LIFECYCLE', message);
    }
};

// Manter compatibilidade com código existente
const debugLog = (message: string, ...args: any[]) => {
    apiLogger.info(message, args.length > 0 ? args : undefined);
};

// Função para limpar resources de uma requisição
const cleanupRequest = (
    requestKey: string,
    pendingRequests: any,
    abortControllers: any,
    retryCount: any
) => {
    // Remover da lista de requisições pendentes
    if (requestKey in pendingRequests.current) {
        delete pendingRequests.current[requestKey];
    }

    // Remover o controller se existir
    if (requestKey in abortControllers.current) {
        delete abortControllers.current[requestKey];
    }

    // Remover contador de retentativas
    if (requestKey in retryCount.current) {
        delete retryCount.current[requestKey];
    }
};

// Funções para manipulação de cookies seguros
const CookieUtils = {
    // Configurações básicas do cookie
    getOptions: (expiresInMinutes = API_CONFIG.TOKEN_EXPIRATION_MINUTES) => {
        return {
            expires: new Date(new Date().getTime() + expiresInMinutes * 60 * 1000),
            secure: API_CONFIG.USE_SECURE_COOKIES, // Cookies seguros em HTTPS
            sameSite: "strict" as "strict", // Proteção contra CSRF
            path: "/",
        };
    },

    // Salvar token no cookie (com criptografia adicional)
    setToken: async (tokenData: any) => {
        try {
            const encryptedToken = await EncryptionUtils.encrypt(
                JSON.stringify(tokenData),
                API_CONFIG.SECRET_KEY
            );

            // Salvar em cookie em vez de localStorage
            Cookies.set(
                API_CONFIG.AUTH_TOKEN_KEY,
                JSON.stringify(encryptedToken),
                CookieUtils.getOptions()
            );

            return true;
        } catch (error) {
            console.error("Erro ao salvar token:", error);
            return false;
        }
    },

    // Obter token do cookie
    getToken: async () => {
        try {
            const tokenEncrypted = Cookies.get(API_CONFIG.AUTH_TOKEN_KEY);
            if (!tokenEncrypted) return null;

            const decryptedData = await EncryptionUtils.decrypt(
                JSON.parse(tokenEncrypted),
                API_CONFIG.SECRET_KEY
            );

            return JSON.parse(decryptedData);
        } catch (error) {
            console.error("Erro ao recuperar token:", error);
            return null;
        }
    },

    // Remover token do cookie
    removeToken: () => {
        Cookies.remove(API_CONFIG.AUTH_TOKEN_KEY, { path: "/" });
    },

    // Gerar token CSRF
    generateCsrfToken: () => {
        const token =
            Math.random().toString(36).substring(2) + Date.now().toString(36);
        Cookies.set("csrf_token", token, CookieUtils.getOptions(60)); // 60 minutos
        return token;
    },

    // Obter token CSRF
    getCsrfToken: () => {
        return Cookies.get("csrf_token") || CookieUtils.generateCsrfToken();
    },
};

// Custom hook para data fetching e autenticação com suporte a cache
export const useDataManagement = () => {
    const [load, setLoad] = useState({ isLoading: false, reason: "" });
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const pendingRequests = useRef<{ [key: string]: Promise<any> }>({});
    const abortControllers = useRef<{ [key: string]: AbortController }>({});
    const retryCount = useRef<{ [key: string]: number }>({});
    // const MAX_RETRIES = 2;  // Número máximo de tentativas para requisições importantes

    // Limpar controllers ao desmontar o componente
    useEffect(() => {
        // Log inicial com informações do ambiente
        apiLogger.info(`🌐 Host: ${window.location.hostname}`);
        apiLogger.info(`🔗 API URL: ${API_CONFIG.BASE_URL}`);
        apiLogger.info(`🏗️ Ambiente: ${DEBUG_MODE ? 'Desenvolvimento' : AMBIENTE_DEV ? 'Dev Remoto' : 'Produção'}`);

        // Capturar a referência atual dentro do efeito
        const currentAbortControllers = abortControllers.current;

        return () => {
            // Só abortar se a flag estiver ativa
            if (!ABORT_ON_UNMOUNT) {
                return;
            }

            // Verificar se cada controller ainda existe e se não foi abortado antes de abortar
            const count = Object.keys(currentAbortControllers).length;
            if (count > 0) {
                apiLogger.lifecycle(`Desmontando: cancelando ${count} requisição(ões)`);
            }
            Object.values(currentAbortControllers).forEach((controller) => {
                try {
                    if (controller && controller.signal && !controller.signal.aborted) {
                        controller.abort();
                    }
                } catch (error) {
                    // Ignora erros de abortamento
                }
            });
        };
    }, []);

    // Verificar se um cache ainda é válido
    const isCacheValid = useCallback(
        (cacheKey: string, maxAge: number): boolean => {
            const cache = globalCache[cacheKey];
            if (!cache) return false;

            const now = Date.now();
            return now - cache.timestamp < maxAge;
        },
        []
    );

    // Obter dados do cache
    const getFromCache = useCallback((cacheKey: string): any | null => {
        return globalCache[cacheKey]?.data || null;
    }, []);

    // Armazenar dados no cache
    const setInCache = useCallback((cacheKey: string, data: any): void => {
        if (!data) return;

        globalCache[cacheKey] = {
            data,
            timestamp: Date.now(),
        };
    }, []);

    // Gerenciamento seguro de token
    const getStoredToken = useCallback(async () => {
        return await CookieUtils.getToken();
    }, []);

    // Método fetch centralizado com tratamento de erros e suporte a cache
    const fetchData = useCallback(
        async (
            route: string,
            data: any = null,
            method: string = "GET",
            includeUserId: boolean = false,
            reason: string = "Carregando dados",
            useCache: boolean = true,
            cacheTime: number = 60000, // Default: 1 minuto
            isRetry: boolean = false
        ) => {
            // Criar uma chave única para esta requisição
            const requestKey = `${method}:${route}:${JSON.stringify(
                data
            )}:${includeUserId}`;

            // Verificar se há uma requisição pendente com essa chave
            // @ts-ignore - O linter está avisando incorretamente sobre uma condição que sempre retorna true
            if (!isRetry && pendingRequests.current.hasOwnProperty(requestKey)) {
                apiLogger.pendingReuse(method, route);
                return pendingRequests.current[requestKey];
            }

            // Verificar o cache para requisições GET
            if (method === "GET" && useCache && isCacheValid(requestKey, cacheTime)) {
                apiLogger.cacheHit(method, route);
                const cachedData = getFromCache(requestKey);
                return Promise.resolve(cachedData);
            }

            // Mostrar loading apenas para requisições iniciais (não retries)
            if (!isRetry) {
                setLoad({ isLoading: true, reason: reason });
                setError(null);
            }

            // Criar um AbortController para esta requisição
            const controller = new AbortController();
            abortControllers.current[requestKey] = controller;

            // Gerar ID único para esta requisição
            const reqId = apiLogger.newRequestId();
            const startTime = Date.now();

            apiLogger.startRequest(reqId, method, route, data);

            // Criamos uma promessa que será armazenada e retornada
            const requestPromise = (async () => {
                try {
                    const token = await getStoredToken();
                    const csrfToken = CookieUtils.getCsrfToken();

                    const url = new URL(`${API_CONFIG.BASE_URL}/${route}`);
                    if (includeUserId && token?.id) {
                        url.pathname += token.id;
                    }

                    const options: RequestInit = {
                        method,
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRF-Token": csrfToken,
                            ...(token?.token
                                ? {
                                    Authorization: `Bearer ${token.token}`,
                                    uid: token.id,
                                }
                                : {}),
                        },
                        signal: controller.signal,
                        ...(data && ["POST", "PUT", "PATCH"].includes(method)
                            ? { body: JSON.stringify(data) }
                            : {}),
                    };

                    // Usar HTTPS em produção
                    if (
                        process.env.NODE_ENV === "production" &&
                        url.protocol === "http:"
                    ) {
                        url.protocol = "https:";
                    }

                    apiLogger.sending(reqId, url.toString());
                    const response = await fetch(url.toString(), options);

                    // Verificar se a resposta é JSON antes de tentar parsear
                    let result;
                    const contentType = response.headers.get("content-type");
                    if (contentType && contentType.includes("application/json")) {
                        result = await response.json();
                    } else {
                        result = { message: await response.text() };
                    }

                    const duration = Date.now() - startTime;
                    apiLogger.response(reqId, method, route, response.status, response.ok, duration);
                    apiLogger.responseData(reqId, result);

                    // Limpar recursos alocados para esta requisição
                    cleanupRequest(
                        requestKey,
                        pendingRequests,
                        abortControllers,
                        retryCount
                    );

                    // Atualizar estado de loading apenas para requisições iniciais
                    if (!isRetry) {
                        setLoad({ isLoading: false, reason: "" });
                    }

                    if (response.status === 401) {
                        const msg = result.message || "Acesso não autorizado FE";
                        apiLogger.auth(reqId, `Token expirado/inválido: ${msg}`);
                        CookieUtils.removeToken();
                        navigate("/login", { replace: true, state: { message: msg } });
                        apiLogger.endRequest(reqId, false);
                        return { response, result: null };
                    }

                    const responseData = { response, result };

                    // Armazenar no cache apenas requisições GET bem-sucedidas
                    if (method === "GET" && response.ok && useCache) {
                        setInCache(requestKey, responseData);
                    }

                    apiLogger.endRequest(reqId, true);
                    return responseData;
                } catch (error) {
                    // Limpar recursos alocados para esta requisição
                    cleanupRequest(
                        requestKey,
                        pendingRequests,
                        abortControllers,
                        retryCount
                    );

                    // Verificar se o erro é de abortamento
                    if (error instanceof DOMException && error.name === "AbortError") {
                        apiLogger.aborted(reqId, route);
                        apiLogger.endRequest(reqId, false);
                        return { response: null, result: null, aborted: true };
                    }

                    // Só atualizar loading para requisições iniciais
                    if (!isRetry) {
                        setLoad({ isLoading: false, reason: "" });
                    }

                    const errorMessage =
                        error instanceof TypeError && error.message === "Failed to fetch"
                            ? "Não foi possível conectar ao servidor. Verifique sua conexão."
                            : error instanceof Error
                                ? error.message
                                : "Erro desconhecido FE";

                    // Só atualiza o estado de erro para requisições iniciais (não retries)
                    if (!isRetry) {
                        setError(errorMessage);
                    }

                    apiLogger.error(reqId, `${method} /${route}`, error);
                    apiLogger.endRequest(reqId, false);
                    return { response: null, result: null, error: errorMessage };
                }
            })();

            // Armazenar a promessa apenas para requisições iniciais
            if (!isRetry) {
                pendingRequests.current[requestKey] = requestPromise;
            }

            return requestPromise;
        },
        [getStoredToken, navigate, isCacheValid, getFromCache, setInCache]
    );

    // Invalidar cache específico
    const invalidateCache = useCallback((pattern: string) => {
        const keysToInvalidate: string[] = [];

        Object.keys(globalCache).forEach((key) => {
            if (key.includes(pattern)) {
                keysToInvalidate.push(key);
            }
        });

        if (keysToInvalidate.length > 0) {
            apiLogger.cacheInvalidate(pattern, keysToInvalidate.length);

            keysToInvalidate.forEach((key) => {
                delete globalCache[key];
            });
        }
    }, []);

    // Limpar todo o cache
    const clearAllCache = useCallback(() => {
        const cacheSize = Object.keys(globalCache).length;
        if (cacheSize > 0) {
            apiLogger.cacheInvalidate('ALL', cacheSize);
            Object.keys(globalCache).forEach((key) => delete globalCache[key]);
        }
    }, []);

    // Autenticação
    const login = useCallback(
        async (email: string, password: string) => {
            const { response, result } = await fetchData(
                "login/",
                { email, password },
                "POST",
                false,
                "Autenticando usuário",
                false // Não usar cache para login
            );

            if (response?.status === 200 && result) {
                // Salvar o token em cookie seguro em vez de localStorage
                await CookieUtils.setToken({
                    token: result.token,
                    id: result.user.id,
                });

                // Limpar todo o cache após login
                Object.keys(globalCache).forEach((key) => delete globalCache[key]);
                return { ok: true };
            }

            return { ok: false, message: result?.message || "Falha no login FE" };
        },
        [fetchData]
    );

    // Logout com limpeza de cache
    const logout = useCallback(() => {
        // Remover token do cookie
        CookieUtils.removeToken();

        // Limpar todo o cache global
        Object.keys(globalCache).forEach((key) => delete globalCache[key]);

        debugLog("Logout realizado: token removido e cache limpo");

        // Redirecionar para a página inicial
        navigate("/", { replace: true });

        return { ok: true };
    }, [navigate]);

    // Rotação de chaves (simulado - a implementação real depende do backend)
    const rotateKey = useCallback(async () => {
        try {
            const token = await getStoredToken();
            if (!token) return { ok: false, message: "Nenhum token disponível" };

            // Aqui teríamos uma chamada ao backend para rotacionar a chave
            // Por enquanto, apenas reencriptamos o token com a chave atual como demonstração
            await CookieUtils.setToken(token);

            return { ok: true };
        } catch (error) {
            console.error("Erro ao rotacionar chave:", error);
            return { ok: false, message: "Erro ao rotacionar chave" };
        }
    }, [getStoredToken]);

    // CRUD genérico com suporte a cache
    const createResource = useCallback(
        async (resource: string, data: any, reason: string) => {
            const {
                response,
                result,
                error: fetchError,
            } = await fetchData(resource, data, "POST", false, reason, false);
            // Invalidar caches relacionados ao recurso após criação
            invalidateCache(resource);

            if (fetchError) {
                return { ok: false, message: fetchError };
            }

            return response?.status === 201 || response?.status === 200
                ? { ok: true, data: result }
                : { ok: false, message: result?.message, result: result || null };
        },
        [fetchData, invalidateCache]
    );

    const updateResource = useCallback(
        async (
            resource: string,
            id: string,
            data: any,
            reason: string,
            includeUserId: boolean = false
        ) => {
            const {
                response,
                result,
                error: fetchError,
            } = await fetchData(
                `${resource}${id}`,
                data,
                "PUT",
                includeUserId,
                reason,
                false
            );
            // Invalidar caches relacionados ao recurso após atualização
            invalidateCache(resource);

            if (fetchError) {
                return { ok: false, message: fetchError };
            }

            return response?.ok
                ? { ok: true, data: result }
                : {
                    ok: false,
                    message: result?.message || `Erro ao atualizar ${resource} FE`,
                };
        },
        [fetchData, invalidateCache]
    );

    const listResources = useCallback(
        async (
            resource: string,
            reason: string,
            params: string = "",
            includeUserId: boolean = false,
            cacheTime: number = 60000
        ) => {
            // Determinar o tempo de cache apropriado baseado no tipo de recurso
            let appropriateCacheTime = cacheTime;
            if (resource.includes("card")) {
                appropriateCacheTime = CACHE_TIMES.APPOINTMENTS;
            } else if (resource.includes("service")) {
                appropriateCacheTime = CACHE_TIMES.SERVICES;
            } else if (resource.includes("user")) {
                appropriateCacheTime = CACHE_TIMES.USERS;
            }

            const {
                response,
                result,
                error: fetchError,
                aborted,
            } = await fetchData(
                `${resource}${params ? "/" + params : ""}`,
                null,
                "GET",
                includeUserId,
                reason,
                true, // Usar cache
                appropriateCacheTime
            );

            if (aborted) {
                return { ok: false, aborted: true };
            }

            if (fetchError) {
                return { ok: false, message: fetchError };
            }

            return response?.status === 200
                ? { ok: true, data: result }
                : { ok: false, message: result?.message };
        },
        [fetchData]
    );

    // Métodos específicos para recursos usando métodos CRUD genéricos
    const userMethods = useMemo(
        () => ({
            create: (user: any) => {
                const result = createResource("user/", user, "Criando usuário");
                invalidateCache("user");
                return result;
            },
            list: () =>
                listResources(
                    "user/",
                    "Listando usuários",
                    "",
                    false,
                    CACHE_TIMES.USERS
                ),
            listClientLevel: () =>
                listResources(
                    "user/clientLevel",
                    "Listando usuários",
                    "",
                    false,
                    CACHE_TIMES.USERS
                ),
            getCurrentUser: () =>
                listResources("user/", "Buscando usuário atual", "", true, 0), // Usando 0 como tempo de cache para nunca usar cache
            update: (id: string, data: any) => {
                const result = updateResource("user/", id, data, "Atualizando usuário");
                invalidateCache("user");
                return result;
            },
            inativeUser: async (id: string) => {
                const {
                    response,
                    result,
                    error: fetchError,
                } = await fetchData(
                    `user/${id}/status`,
                    {},
                    "PUT",
                    false,
                    "Inativando usuário",
                    false
                );
                invalidateCache("user");
                if (fetchError) {
                    return { ok: false, message: fetchError };
                }

                return response?.ok
                    ? { ok: true, data: result }
                    : {
                        ok: false,
                        message: result?.message || `Erro ao inativar funcionário FE`,
                    };
            },
            updateCurrent: (data: any) => {
                const result = updateResource(
                    "user/",
                    "",
                    data,
                    "Atualizando usuário",
                    true
                );
                invalidateCache("user");
                return result;
            },
            changePassword: (email: any) =>
                createResource(`forgot-password/`, email, "Redefinindo senha"),
        }),
        [createResource, listResources, updateResource, invalidateCache, fetchData]
    );

    const serviceMethods = useMemo(
        () => ({
            create: (service: any) => {
                const result = createResource("service/", service, "Criando serviço");
                invalidateCache("service");
                return result;
            },
            list: () =>
                listResources(
                    "service/",
                    "Listando serviços",
                    "",
                    false,
                    CACHE_TIMES.SERVICES
                ),
            listInactive: () =>
                listResources(
                    "service/?status=inactive/",
                    "Listando serviços inativos",
                    "",
                    false,
                    CACHE_TIMES.SERVICES
                ),
            listAll: () =>
                listResources(
                    "service/?status=all",
                    "Listando serviços ativos e inativos",
                    "",
                    false,
                    CACHE_TIMES.SERVICES
                ),
            update: (id: string, data: any) => {
                const result = updateResource(
                    "service/",
                    id,
                    data,
                    "Atualizando serviço"
                );
                invalidateCache("service");
                return result;
            },
            toggleStatus: async (id: string) => {
                const {
                    response,
                    result,
                    error: fetchError,
                } = await fetchData(
                    `service/${id}/status/`,
                    {},
                    "PUT",
                    false,
                    "Alterando status",
                    false
                );
                // Invalidar caches relacionados ao recurso após atualização
                invalidateCache("service");

                if (fetchError) {
                    return { ok: false, message: fetchError };
                }

                return response?.ok
                    ? { ok: true, data: result }
                    : {
                        ok: false,
                        message: result?.message || `Erro ao atualizar service FE`,
                    };
            },
        }),
        [createResource, listResources, updateResource, fetchData, invalidateCache]
    );

    const appointmentMethods = useMemo(
        () => ({
            create: (appointment: any) => {
                const result = createResource(
                    "card/",
                    appointment,
                    "Criando agendamento"
                );
                invalidateCache("card");
                return result;
            },
            createBulk: (appointment: any) => {
                const result = createResource(
                    "card/bulk",
                    appointment,
                    "Criando agendamentos em lote"
                );
                invalidateCache("card");
                return result;
            },
            list: () =>
                listResources(
                    "card/",
                    "Listando agendamentos",
                    "",
                    false,
                    CACHE_TIMES.APPOINTMENTS
                ),
            listMonth: (mes: string, year: string, user?: string) =>
                listResources(
                    "card/month?" + mes + "&year=" + year + (user ? "&user=" + user : ""),
                    "Listando agendamentos",
                    "",
                    false,
                    CACHE_TIMES.APPOINTMENTS
                ),
            listClientLevel: () =>
                listResources(
                    "card/clientLevel",
                    "Listando agendamentos para clientes",
                    "",
                    false,
                    CACHE_TIMES.APPOINTMENTS
                ),
            getCardsClientLevel: (user: any) =>
                listResources(
                    `card/clientLevel?user=${user}`,
                    `Listando agendamentos para clientes`,
                    "",
                    false,
                    CACHE_TIMES.APPOINTMENTS
                ),
            get: (id: string) =>
                listResources(`card/${id}`, "Buscando agendamento", "", false, 0), // Usando 0 como tempo de cache para nunca usar cache
            getBulk: (id: string) =>
                listResources(
                    `card/bulk/${id}`,
                    "Buscando agendamentos em lote",
                    "",
                    false,
                    0
                ),
            update: (id: string, data: any) => {
                const result = updateResource(
                    "card/",
                    id,
                    data,
                    "Atualizando agendamento"
                );
                invalidateCache("card");
                return result;
            },
            updateBulk: (id: string, data: any) => {
                const result = updateResource(
                    "card/bulk/",
                    id,
                    data,
                    "Atualizando agendamentos em lote"
                );
                invalidateCache("card");
                return result;
            },
            deleteBulk: async (id: string) => {
                const {
                    response,
                    result,
                    error: fetchError,
                } = await fetchData(
                    `card/bulk/${id}`,
                    {},
                    "DELETE",
                    false,
                    "Cancelando agendamentos em lote",
                    false
                );
                invalidateCache("card");
                if (fetchError) {
                    return { ok: false, message: fetchError };
                }

                return response?.ok
                    ? { ok: true, data: result }
                    : {
                        ok: false,
                        message:
                            result?.message || `Erro ao cancelar agendamentos em lote FE`,
                    };
            },
            inactivate: async (id: string) => {
                const {
                    response,
                    result,
                    error: fetchError,
                } = await fetchData(
                    `card/${id}`,
                    {},
                    "DELETE",
                    false,
                    "Cancelando agendamento",
                    false
                );
                invalidateCache("card");
                if (fetchError) {
                    return { ok: false, message: fetchError };
                }

                return response?.ok
                    ? { ok: true, data: result }
                    : {
                        ok: false,
                        message: result?.message || `Erro ao inativar funcionário FE`,
                    };
            },
        }),
        [createResource, listResources, updateResource, fetchData, invalidateCache]
    );

    const reportMethods = useMemo(() => {
        interface generalFilters {
            start_date?: string; // string, opcional (YYYY-MM-DD)
            final_date?: string; // string, opcional (YYYY-MM-DD)
            page?: number; // integer, opcional, padrão: 1
            limit?: number; // integer, opcional, padrão: 10
        }
        interface revenueFilters extends generalFilters {
            users?: number[]; // array de integers, opcional (IDs de Usuário)
        }

        interface appointmentsFilters extends generalFilters {
            customers?: string[]; // array de strings, opcional
            users?: number[]; // array de integers, opcional (IDs de Usuário)
        }

        interface clientFilters extends generalFilters {
            customers?: string[]; // array de strings, opcional
        }

        interface servicesFilters extends generalFilters {
            services?: string[]; // array de strings, opcional
        }

        return {
            revenue: (filters: revenueFilters) =>
                createResource(
                    "report/revenue",
                    filters,
                    "Gerando relatório de receita"
                ),
            appointments: (filters: appointmentsFilters) =>
                createResource(
                    "report/card",
                    filters,
                    "Gerando relatório de agendamentos"
                ),
            services: (filters: servicesFilters) =>
                createResource(
                    "report/service",
                    filters,
                    "Gerando relatório de serviços"
                ),
            clients: (filters: clientFilters) =>
                createResource(
                    "report/client",
                    filters,
                    "Gerando relatório de clientes"
                ),
        };
    }, [createResource]);

    const parseCustomizationResponse = useCallback(
        (responsePromise: Promise<any>): Promise<any> => {
            return responsePromise.then((response) => {
                if (
                    response.ok &&
                    response.data?.json &&
                    typeof response.data.json === "string"
                ) {
                    try {
                        response.data = JSON.parse(response.data.json);
                    } catch (e) {
                        console.error("Failed to parse response.data.json", e);
                    }
                }
                return response;
            });
        },
        []
    );

    const customizeMethods = useMemo(
        () => ({
            create: (reference: string, json: string) => {
                const result = createResource(
                    "customize/",
                    { reference, json },
                    "Criando customização"
                );
                invalidateCache("customize");
                return result;
            },
            list: () =>
                parseCustomizationResponse(
                    listResources(
                        "customize/default_customize",
                        "Listando customizações",
                        "",
                        false,
                        0
                    )
                ),
            logo_primary: () =>
                parseCustomizationResponse(
                    listResources(
                        "customize/logo_primary",
                        "Carregando logo primária",
                        "",
                        false,
                        0
                    )
                ),
            logo_secondary: () =>
                parseCustomizationResponse(
                    listResources(
                        "customize/logo_secondary",
                        "Carregando logo secundária",
                        "",
                        false,
                        0
                    )
                ),
        }),
        [createResource, listResources, invalidateCache, parseCustomizationResponse]
    );

    return {
        login,
        logout,
        rotateKey,
        users: userMethods,
        services: serviceMethods,
        appointments: appointmentMethods,
        reports: reportMethods,
        customization: customizeMethods,
        load,
        error,
        invalidateCache,
        clearAllCache,
    };
};
